import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getTeamById, updateTeam, deleteTeam } from '../../api/authenticateAPIs';
import { getEnvironmentsforUser } from '../../api/documentAPI';
import { useSelectedTeamIdAtom } from '../../atoms/teams/useSelectedTeamIdAtom';
import { useNotification } from '../../context/NotificationContext';
import { NotificationType } from '../../enums/NotificationType';
import { useManageDocsEnvironmentAtom } from '../../atoms/managedocs/useManageDocsEnvironmentAtom';
import UsersMultiselect from '../../components/shared/UsersMultiselect';
import EnvironmentsMultiselect from '../../components/shared/EnvironmentsMultiSelect';
import AccessMultiselect from '../../components/shared/AccessMultiselect';
import ConfirmDeleteModal from '../../components/shared/ConfirmDeleteModal';


const EditTeam = () => {

    const { showNotification } = useNotification();

    const [t] = useTranslation('global');

    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({id : undefined, name: '', envIds: [], userIds: [], adDn: '', accessIds: []});
  
    const [selectedTeamId, setSelectedTeamId] = useSelectedTeamIdAtom();
    const [manageDocsEnvironmentId, setManageDocsEnvironmentId] = useManageDocsEnvironmentAtom();

    const navigate = useNavigate();

    const handleOpen = () => setOpen(true);

    const handleClose = () => setOpen(false);

    const fetchTeam = async (id) => {
        try {
            const response = await getTeamById(id);
            setFormData({
                id: response.id,
                name: response.name,
                adDn: response.adDn || '',
                envIds: response.environments.map(e => e.id),
                userIds: response.users.map(u => u.id),
                accessIds: response.accesses ? response.accesses.map(a => a.accessId) : []
            });
        } catch (error) {
            console.error("Error fetching data: ", error);
        }
    }

    const validateFormData = async() => {
        if(!formData.name){
            showNotification({ message: t('teamNameRequired'), type: NotificationType.Error });
            return ;
        }
        // if(!formData.envIds.length > 0) {
        //     showNotification({ message: t('environmentsRequired'), type: NotificationType.Error });
        //     return;
        // }
        if(!formData.userIds.length > 0) {
            showNotification({ message: t('usersRequired'), type: NotificationType.Error });
            return;
        }
        
        return true;
    }

    useEffect(()=> {
        if (selectedTeamId) {
            fetchTeam(selectedTeamId);
        } else {
            navigate('/Teams');
        }
    }, []);

    const handleUpdate = async(e) => {
        e.preventDefault();
        setIsLoading(true);

        if (validateFormData()) {
            try {
                const res = await updateTeam(formData.id, formData.name, formData.userIds, formData.envIds, formData.adDn, formData.accessIds);
                console.log(res);
                showNotification({ message: t('savedChanges'), type: NotificationType.Success });

                if(manageDocsEnvironmentId != undefined){ // clear selected docEnv if is not belong user anymore
                    const environments = await getEnvironmentsforUser();
                    if(environments.find(item => item.Id == manageDocsEnvironmentId) == undefined){
                        setManageDocsEnvironmentId(null)
                    }
                }
            } catch (error) {
                console.error("Error updating team: ", error); 
            } finally {
                setIsLoading(false);
            }
        } else {
            showNotification({ message: t('errorOccured'), type: NotificationType.Warning });
            setIsLoading(false); 
        }
    };
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    } 

    const handleUserChange = (event) => {
        const { target: { value },} = event;
        setFormData({...formData,
            userIds: typeof value === 'string' ? value.split(',') : value,
        });
    }; 

    const handleEnvironmentChange = (event) => {
        const { target: { value },} = event;
        setFormData({...formData,
            envIds: typeof value === 'string' ? value.split(',') : value,
        });
    };

    const handleAccessChange = (event) => {
        const { target: { value },} = event;
        setFormData({...formData,
            accessIds: typeof value === 'string' ? value.split(',') : value,
        });
    };

    const handleGoBack = () => {
        setSelectedTeamId(null);
        navigate('/Teams');
    };

    const handleNo = () => {
        handleClose();
    }

    const handleYes = () => {
        handleDelete();
        handleClose();
    }

    const handleDelete = () => {
        deleteTeam(selectedTeamId)
         .then(r => {
            showNotification({ message: 'Team removed successfully.', type: NotificationType.Success });
            })
            .catch(error => {
                console.error(error);
                setIsLoading(false); 
            })
            .finally(() => {
                setIsLoading(false);
                navigate('/Teams');
            });
    }

    return (
        <div className='page-wrap'>
            <div className="header-title">
                <h1>{t('editTeam')}</h1>
            </div> 
        
            <div className='input-form'>
                <div className='input-line'>
                <label htmlFor='name'>{t('teamName')}</label>
                    <input 
                        type="text" 
                        id='name'
                        name='name'
                        value={formData.name} 
                        onChange={handleChange}
                    />
                </div>

                <div className='input-line'>
                    <UsersMultiselect
                        selectedValues={formData.userIds}
                        onChange={handleUserChange}
                    />
                </div>

                <div className='input-line'>
                    <EnvironmentsMultiselect
                        selectedValues={formData.envIds}
                        onChange={handleEnvironmentChange}
                    />
                </div>

                <div className='input-line'>
                    <AccessMultiselect
                        selectedValues={formData.accessIds}
                        onChange={handleAccessChange}
                    />
                </div>

                <div className='input-line'>
                <label htmlFor='adDn'>{t('adGroupNumber')}</label>
                    <input 
                        type="text" 
                        id='adDn'
                        name='adDn'
                        value={formData.adDn} 
                        onChange={handleChange}
                    />
                </div>

            </div>

            <button className="return-button" onClick={handleGoBack}>{t('return')}</button>
            <button className='submit-button' onClick={handleUpdate}>{t('save')}</button>
            <button className="delete-button" type="submit" onClick={(e) => handleOpen(e)} disabled={isLoading}>{t('delete')}</button>

            <ConfirmDeleteModal 
                open={open} 
                onNo={handleNo}
                onYes={handleYes}
                onClose={handleClose}
                item={formData.name}
            /> 
        </div>
    );
}
export default EditTeam;